<script setup lang="ts">
/**
 * One task in an execution, as the portal's graph draws it (apps/portal/src/components/ExecutionGraphNode.vue),
 * in the brand's poster style: a square card with a 1.5px border in its status tone, the owner's avatar,
 * the task and its slug, the owner and team lines, and the status as a glyph and a word in the footer with
 * the run count. Round handles on the left and right edges, in the tone. Completed nodes take a faint green
 * ground; a running node pulses on the run clock (BG §22.2), never faster.
 *
 * Data in, drawing out (lib/surfaces.ts ExecutionNodeView): the portal feeds it an ExecutionGraphNode, the
 * site the example company. It fills its box; size it with `--u` (one canvas unit; 1px by default).
 * The status glyph is drawn in CSS, so what a screen reader hears is the word.
 *
 * Slots, for the portal's live parts: `avatar` (a member picture that previews), `badge` (a marker at the
 * head's end), and `runs` (the count, given as `text`, to wrap in a tooltip). Selecting, focus, and the
 * canvas's own handles belong to the caller.
 */
import { computed } from 'vue'
import { EXECUTION_TONE_WORD, type ExecutionNodeView, initials } from '../lib/surfaces'

const props = defineProps<{ node: ExecutionNodeView }>()

const word = computed(() => props.node.label ?? EXECUTION_TONE_WORD[props.node.tone])
const owner = computed(
    () => [props.node.member?.name, props.node.role?.name].filter(Boolean).join(' / ') || 'Unassigned',
)
const team = computed(() =>
    [props.node.team?.name, props.node.missing && 'Unavailable task'].filter(Boolean).join(' / '),
)
const runs = computed(() => {
    const count = `${props.node.runCount} ${props.node.runCount === 1 ? 'run' : 'runs'}`
    const active = props.node.activeCount ?? 0
    return active > 1 ? `${count} / ${active} current` : count
})
</script>

<template>
    <div class="zh-exec-node" :data-tone="node.tone" :data-missing="node.missing || undefined">
        <span class="zh-exec-node__handle zh-exec-node__handle--in" aria-hidden="true" />
        <span class="zh-exec-node__handle zh-exec-node__handle--out" aria-hidden="true" />
        <div class="zh-exec-node__head">
            <span class="zh-exec-node__avatar" aria-hidden="true">
                <slot name="avatar">
                    <img v-if="node.member?.avatarUrl" :src="node.member.avatarUrl" alt="" />
                    <template v-else>{{ node.member ? initials(node.member.name) : '' }}</template>
                </slot>
            </span>
            <span class="zh-exec-node__names">
                <span class="zh-exec-node__task">{{ node.name }}</span>
                <span class="zh-exec-node__slug">{{ node.slug }}</span>
            </span>
            <span v-if="$slots.badge" class="zh-exec-node__badge"><slot name="badge" /></span>
        </div>
        <p class="zh-exec-node__owner">{{ owner }}</p>
        <p class="zh-exec-node__team">{{ team }}</p>
        <p class="zh-exec-node__foot">
            <span class="zh-exec-node__state">{{ word }}</span>
            <span class="zh-exec-node__runs"><slot name="runs" :text="runs">{{ runs }}</slot></span>
        </p>
    </div>
</template>

<style scoped>
.zh-exec-node {
    --tone: var(--state-inactive);
    position: relative;
    display: flex;
    box-sizing: border-box;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: calc(10 * var(--u, 1px)) calc(11 * var(--u, 1px));
    border: 1.5px solid var(--tone);
    background: var(--panel);
    color: var(--ink-1);
}

.zh-exec-node[data-tone="succeeded"] {
    --tone: var(--state-complete);
    background: color-mix(in srgb, var(--state-complete) 5%, var(--panel));
}

.zh-exec-node[data-tone="running"] {
    --tone: var(--state-active);
}

.zh-exec-node[data-tone="action"] {
    --tone: var(--state-action);
    background: color-mix(in srgb, var(--state-action) 5%, var(--panel));
}

.zh-exec-node[data-tone="failed"] {
    --tone: var(--state-failed);
    background: color-mix(in srgb, var(--state-failed) 4%, var(--panel));
}

/* Not visited, and cancelled or skipped: dashed, a step quieter. Paused and queued keep a solid edge. */
.zh-exec-node[data-tone="unvisited"],
.zh-exec-node[data-tone="cancelled"] {
    border-style: dashed;
    color: var(--ink-2);
}

/* A task that has gone keeps its tone, dashed. */
.zh-exec-node[data-missing] {
    border-style: dashed;
}

.zh-exec-node__handle {
    position: absolute;
    top: 50%;
    width: calc(9 * var(--u, 1px));
    height: calc(9 * var(--u, 1px));
    border: 2px solid var(--tone);
    border-radius: 50%;
    background: var(--panel);
    translate: 0 -50%;
}

.zh-exec-node__handle--in {
    left: calc(-5.5 * var(--u, 1px));
}

.zh-exec-node__handle--out {
    right: calc(-5.5 * var(--u, 1px));
}

.zh-exec-node__head {
    display: flex;
    align-items: flex-start;
    gap: calc(8 * var(--u, 1px));
}

.zh-exec-node__avatar {
    display: grid;
    flex-shrink: 0;
    place-items: center;
    width: calc(28 * var(--u, 1px));
    height: calc(28 * var(--u, 1px));
    border: 1.5px solid var(--line-strong);
    overflow: hidden;
    border-radius: 50%;
    background: var(--void-black);
    font-family: var(--font-mono);
    font-size: calc(9 * var(--u, 1px));
    font-weight: 500;
}

.zh-exec-node__avatar img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
}

.zh-exec-node__names {
    display: grid;
    flex: 1;
    min-width: 0;
}

.zh-exec-node__badge {
    display: inline-flex;
    flex-shrink: 0;
    color: var(--ink-3);
}

/* Up to two lines, as the portal's node allows. */
.zh-exec-node__task {
    display: -webkit-box;
    overflow: hidden;
    font-size: calc(13.5 * var(--u, 1px));
    font-weight: 600;
    line-height: 1.2;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}

.zh-exec-node__slug {
    overflow: hidden;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: calc(9.5 * var(--u, 1px));
    text-overflow: ellipsis;
    white-space: nowrap;
}

.zh-exec-node__owner,
.zh-exec-node__team {
    margin: 0;
    overflow: hidden;
    font-size: calc(11 * var(--u, 1px));
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.zh-exec-node__owner {
    margin-top: calc(7 * var(--u, 1px));
    color: var(--ink-2);
}

.zh-exec-node__team {
    color: var(--ink-3);
}

.zh-exec-node__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: calc(6 * var(--u, 1px));
    margin: auto 0 0;
    padding-top: calc(6 * var(--u, 1px));
    border-top: 1px solid var(--hair);
    font-family: var(--font-mono);
    font-size: calc(9 * var(--u, 1px));
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    white-space: nowrap;
}

.zh-exec-node__state {
    display: inline-flex;
    align-items: center;
    gap: calc(5 * var(--u, 1px));
    color: var(--tone);
}

/* The status glyph, drawn: ✓ completed, ● running, ▲ waiting on you, ✕ failed; the parked states are a small dot. */
.zh-exec-node__state::before {
    content: "";
    font-size: calc(9 * var(--u, 1px));
}

.zh-exec-node[data-tone="succeeded"] .zh-exec-node__state::before {
    content: "✓" / "";
}

.zh-exec-node[data-tone="running"] .zh-exec-node__state::before {
    content: "●" / "";
}

.zh-exec-node[data-tone="action"] .zh-exec-node__state::before {
    content: "▲" / "";
}

.zh-exec-node[data-tone="failed"] .zh-exec-node__state::before {
    content: "✕" / "";
}

.zh-exec-node[data-tone="unvisited"] .zh-exec-node__state::before,
.zh-exec-node[data-tone="paused"] .zh-exec-node__state::before,
.zh-exec-node[data-tone="queued"] .zh-exec-node__state::before,
.zh-exec-node[data-tone="cancelled"] .zh-exec-node__state::before {
    display: inline-block;
    width: calc(5 * var(--u, 1px));
    height: calc(5 * var(--u, 1px));
    border-radius: 50%;
    background: currentColor;
}

.zh-exec-node__runs {
    color: var(--ink-3);
}

/* BG §22.2: the run pulse, on the execution-map clock. Opacity only, and only a running node. */
.zh-exec-node[data-tone="running"]::after {
    content: "";
    position: absolute;
    inset: -1.5px;
    pointer-events: none;
    box-shadow:
        0 0 0 calc(5 * var(--u, 1px)) color-mix(in srgb, var(--state-active) 12%, transparent),
        0 0 calc(18 * var(--u, 1px)) color-mix(in srgb, var(--state-active) 28%, transparent);
    opacity: 0.5;
}

@media (prefers-reduced-motion: no-preference) {
    .zh-exec-node[data-tone="running"]::after {
        animation: fnode-pulse var(--motion-run-pulse) ease-in-out infinite;
    }

    [data-loop="off"] .zh-exec-node[data-tone="running"]::after {
        animation-play-state: paused;
    }
}

@keyframes fnode-pulse {
    50% {
        opacity: 1;
    }
}
</style>
